// Geolocation utility functions
import { getWebApiBaseUrl } from '../config/apiBaseUrl';

export interface Location {
  latitude: number;
  longitude: number;
  city?: string;
  state?: string;
  stateAbbreviation?: string;
}

// Get user's current location
export const getCurrentLocation = (): Promise<Location> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          // Reverse geocode using OpenStreetMap Nominatim API (free, no API key needed)
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
            {
              headers: {
                'User-Agent': 'BuyDrop/1.0'
              }
            }
          );
          
          const data = await response.json();
          
          const address = data.address || {};
          const city = address.city || address.town || address.village || address.municipality || '';
          const state = address.state || '';
          
          resolve({
            latitude,
            longitude,
            city,
            state,
            stateAbbreviation: getStateAbbreviation(state),
          });
        } catch (error) {
          // If geocoding fails, still return coordinates
          resolve({
            latitude,
            longitude,
          });
        }
      },
      (error) => {
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // Cache for 5 minutes
      }
    );
  });
};

// Convert state name to abbreviation
const getStateAbbreviation = (stateName: string): string => {
  const stateMap: { [key: string]: string } = {
    'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR',
    'California': 'CA', 'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE',
    'Florida': 'FL', 'Georgia': 'GA', 'Hawaii': 'HI', 'Idaho': 'ID',
    'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS',
    'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
    'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
    'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV',
    'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
    'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK',
    'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
    'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT',
    'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV',
    'Wisconsin': 'WI', 'Wyoming': 'WY', 'District of Columbia': 'DC',
  };
  
  return stateMap[stateName] || stateName;
};

// In-memory cache for geocode results (avoids N API calls when many listings share same city)
const geocodeCache = new Map<string, { lat: number; lng: number }>();
const geocodePending = new Map<string, Promise<{ lat: number; lng: number } | null>>();
const GEOCODE_CACHE_MAX = 200;

// Geocode city and state to coordinates
export const geocodeLocation = async (
  city: string,
  state: string
): Promise<{ lat: number; lng: number } | null> => {
  if (!city || !state) return null;
  const cacheKey = `${String(city).toLowerCase().trim()}|${String(state).toLowerCase().trim()}`;
  const cached = geocodeCache.get(cacheKey);
  if (cached) return cached;
  const pending = geocodePending.get(cacheKey);
  if (pending) return pending;

  const promise = (async () => {
    try {
      // Use OpenStreetMap Nominatim API
      const query = `${city}, ${state}, USA`;
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`,
        {
          headers: {
            'User-Agent': 'BuyDrop/1.0'
          }
        }
      );
      const data = await response.json();
      if (data && data.length > 0) {
        const result = {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon)
        };
        if (geocodeCache.size >= GEOCODE_CACHE_MAX) {
          const firstKey = geocodeCache.keys().next().value;
          if (firstKey) geocodeCache.delete(firstKey);
        }
        geocodeCache.set(cacheKey, result);
        return result;
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    } finally {
      geocodePending.delete(cacheKey);
    }
  })();
  geocodePending.set(cacheKey, promise);
  return promise;
};

// Find matching state and city IDs from our database
export const findLocationIds = async (
  cityName: string,
  stateAbbreviation: string
): Promise<{ stateId?: number; cityId?: number }> => {
  try {
    const API_URL = getWebApiBaseUrl();
    
    console.log('Finding location IDs for:', cityName, stateAbbreviation);
    console.log('Using API URL:', API_URL);
    
    // Get states
    const statesResponse = await fetch(`${API_URL}/locations/states`);
    if (!statesResponse.ok) {
      console.error('Failed to fetch states:', statesResponse.status);
      return {};
    }
    const states = await statesResponse.json();
    console.log('States fetched:', states.length);
    
    const state = states.find((s: any) => 
      s.abbreviation === stateAbbreviation || 
      s.name.toLowerCase() === stateAbbreviation.toLowerCase()
    );
    
    if (!state) {
      console.log('State not found for:', stateAbbreviation);
      return {};
    }
    console.log('Found state:', state.name, state.id);
    
    // Get cities for that state
    const citiesResponse = await fetch(`${API_URL}/locations/states/${state.id}/cities`);
    if (!citiesResponse.ok) {
      console.error('Failed to fetch cities:', citiesResponse.status);
      return { stateId: state.id };
    }
    const cities = await citiesResponse.json();
    console.log('Cities fetched:', cities.length);
    
    // Try to find matching city (fuzzy match)
    const city = cities.find((c: any) => 
      c.name.toLowerCase() === cityName.toLowerCase() ||
      c.name.toLowerCase().includes(cityName.toLowerCase()) ||
      cityName.toLowerCase().includes(c.name.toLowerCase())
    );
    
    if (city) {
      console.log('Found city:', city.name, city.id);
    } else {
      console.log('City not found for:', cityName);
    }
    
    return {
      stateId: state.id,
      cityId: city?.id,
    };
  } catch (error) {
    console.error('Error finding location IDs:', error);
    return {};
  }
};

